import ThemeAssetPreloader from "@/services/ThemeAssetPreloader";
import ThemeCacheService from "@/services/ThemeCacheService";
import ThemeService from "@/services/ThemeService";
import ThemeResourceService from "@/services/ThemeResourceService";
import ThemeTokenService from "@/services/ThemeTokenService";

const CLASSIC_THEME = Object.freeze({
  schema_version: 1,
  configuration_version: "classic",
  mode: "classic",
  local_date: "",
  timezone: "Europe/Kyiv",
  next_change_at: null,
  layers: [{ type: "default", code: "default" }],
  config: { schema_version: 1 },
});

const DEFAULT_PREFERENCES = Object.freeze({
  dynamic_themes_enabled: true,
  monthly_changes_enabled: true,
  holiday_themes_enabled: true,
  animations_enabled: true,
  seasonal_mascot_enabled: true,
  theme_mode: "auto",
  global_dynamic_themes_enabled: true,
  effective_mode: "auto",
});

const MAX_REFRESH_DELAY_MS = 24 * 60 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 60 * 1000;

function freshClassicTheme() {
  return JSON.parse(JSON.stringify(CLASSIC_THEME));
}

function freshPreferences() {
  return { ...DEFAULT_PREFERENCES };
}

function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isValidTheme(theme) {
  return Boolean(
    theme &&
      theme.schema_version === 1 &&
      theme.config &&
      theme.config.schema_version === 1 &&
      Array.isArray(theme.layers) &&
      theme.layers.length
  );
}

function userCacheKey(rootGetters) {
  const user = rootGetters["UserStore/getUser"] || {};
  if (!user.userId) return "";
  return `${user.guest ? "guest" : "user"}-${user.userId}`;
}

const state = {
  activeTheme: freshClassicTheme(),
  preferences: freshPreferences(),
  etag: "",
  isInitializing: false,
  isLoadingTheme: false,
  isLoadingPreferences: false,
  initialized: false,
  usingCachedTheme: false,
  errors: {
    theme: null,
    preferences: null,
  },
  preload: {
    total: 0,
    loaded: 0,
    failed: [],
  },
  refreshTimer: null,
  visibilityHandler: null,
  lastRefreshAt: null,
  previewMonthlyCode: "",
  previewHolidayCode: "",
};

const getters = {
  getActiveTheme: (state) => state.activeTheme,
  getConfig: (state) => state.activeTheme.config,
  getLayers: (state) => state.activeTheme.layers,
  getPreferences: (state) => state.preferences,
  getEffectiveMode: (state) => state.preferences.effective_mode,
  getThemeContext: (state) => ({
    ...state.activeTheme,
    preferences: state.preferences,
  }),
  isLoading: (state) => state.isLoadingTheme || state.isLoadingPreferences,
  isInitialized: (state) => state.initialized,
  isUsingCachedTheme: (state) => state.usingCachedTheme,
  getErrors: (state) => state.errors,
  getPreloadState: (state) => state.preload,
  getPreviewCode: (state) =>
    state.previewHolidayCode
      ? `holiday:${state.previewHolidayCode}`
      : state.previewMonthlyCode
      ? `monthly:${state.previewMonthlyCode}`
      : "",
  getPreviewMonthlyCode: (state) => state.previewMonthlyCode,
  getPreviewHolidayCode: (state) => state.previewHolidayCode,
};

const actions = {
  async initialize({ state, commit, dispatch, rootGetters }) {
    if (state.isInitializing) return;
    if (!rootGetters["UserStore/hasToken"]) {
      await dispatch("initializePublic");
      return;
    }
    commit("setInitializing", true);
    const cacheKey = userCacheKey(rootGetters);
    const cached = ThemeCacheService.read(cacheKey);
    if (cached && isValidTheme(cached.activeTheme)) {
      const activeTheme = {
        ...cached.activeTheme,
        config: ThemeResourceService.normalizeUrls(
          cached.activeTheme.config,
          rootGetters["ConfigStore/getServerUrl"]
        ),
      };
      commit("setActiveTheme", activeTheme);
      commit("setEtag", cached.etag || "");
      commit("setUsingCachedTheme", true);
      ThemeTokenService.apply(activeTheme.config.tokens);
      ThemeResourceService.apply(activeTheme.config);
    }
    dispatch("setupLifecycle");
    await dispatch("refresh", { cacheKey });
    commit("setInitialized", true);
    commit("setInitializing", false);
  },

  async initializePublic(
    { state, commit, dispatch, rootGetters },
    previews = {}
  ) {
    if (state.isInitializing) return;
    commit("setInitializing", true);
    commit("setLoadingTheme", true);
    commit("setThemeError", null);
    try {
      const response = await ThemeService.getActiveTheme("", {
        monthly: previews.previewMonthlyCode ?? state.previewMonthlyCode,
        holiday: previews.previewHolidayCode ?? state.previewHolidayCode,
      });
      if (!isValidTheme(response.data)) {
        throw new Error("Invalid public theme response");
      }
      const activeTheme = {
        ...response.data,
        config: ThemeResourceService.normalizeUrls(
          response.data.config,
          rootGetters["ConfigStore/getServerUrl"]
        ),
      };
      commit("setActiveTheme", activeTheme);
      ThemeTokenService.apply(activeTheme.config.tokens);
      ThemeResourceService.apply(activeTheme.config);
      ThemeAssetPreloader.preloadCritical(activeTheme.config).then(
        (preload) => commit("setPreload", preload)
      );
      commit("setInitialized", true);
    } catch (error) {
      commit("setThemeError", error?.message || "Public theme loading failed");
      ThemeTokenService.reset();
      ThemeResourceService.reset();
    } finally {
      commit("setLoadingTheme", false);
      commit("setInitializing", false);
      if (rootGetters["UserStore/hasToken"]) {
        dispatch("initialize");
      }
    }
  },

  async refresh({ state, commit, dispatch, rootGetters }, payload = {}) {
    if (!rootGetters["UserStore/hasToken"]) return;
    const cacheKey = payload.cacheKey || userCacheKey(rootGetters);
    await dispatch("loadPreferences");
    commit("setLoadingTheme", true);
    commit("setThemeError", null);
    try {
      const previewMonthlyCode =
        payload.previewMonthlyCode ?? state.previewMonthlyCode;
      const previewHolidayCode =
        payload.previewHolidayCode ?? state.previewHolidayCode;
      const hasPreview = Boolean(previewMonthlyCode || previewHolidayCode);
      const response = await ThemeService.getActiveTheme(
        hasPreview ? "" : state.etag,
        { monthly: previewMonthlyCode, holiday: previewHolidayCode }
      );
      if (response.status !== 304) {
        if (!isValidTheme(response.data)) {
          throw new Error("Invalid active theme response");
        }
        const activeTheme = {
          ...response.data,
          config: ThemeResourceService.normalizeUrls(
            response.data.config,
            rootGetters["ConfigStore/getServerUrl"]
          ),
        };
        commit("setActiveTheme", activeTheme);
        ThemeTokenService.apply(activeTheme.config.tokens);
        ThemeResourceService.apply(activeTheme.config);
        ThemeAssetPreloader.preloadCritical(activeTheme.config).then(
          (preload) => commit("setPreload", preload)
        );
        const etag = response.headers?.etag || "";
        commit("setEtag", hasPreview ? "" : etag);
        if (!hasPreview) ThemeCacheService.write(cacheKey, activeTheme, etag);
      }
      commit("setUsingCachedTheme", false);
      commit("setLastRefreshAt", new Date().toISOString());
      dispatch("scheduleRefresh");
    } catch (error) {
      commit("setThemeError", error?.message || "Theme loading failed");
      if (!isValidTheme(state.activeTheme)) {
        commit("setActiveTheme", freshClassicTheme());
        ThemeTokenService.reset();
        ThemeResourceService.reset();
      }
    } finally {
      commit("setLoadingTheme", false);
    }
  },

  async loadPreferences({ commit }) {
    commit("setLoadingPreferences", true);
    commit("setPreferencesError", null);
    try {
      const response = await ThemeService.getPreferences();
      commit("setPreferences", response.data);
    } catch (error) {
      commit(
        "setPreferencesError",
        error?.message || "Theme preferences loading failed"
      );
    } finally {
      commit("setLoadingPreferences", false);
    }
  },

  async updatePreferences({ commit, dispatch }, preferences) {
    commit("setLoadingPreferences", true);
    commit("setPreferencesError", null);
    try {
      const response = await ThemeService.updatePreferences(preferences);
      commit("setPreferences", response.data);
      await dispatch("refresh");
      return response.data;
    } catch (error) {
      commit(
        "setPreferencesError",
        error?.message || "Theme preferences saving failed"
      );
      throw error;
    } finally {
      commit("setLoadingPreferences", false);
    }
  },

  async setPreview({ commit, dispatch }, previewCode) {
    const [type, code] = (previewCode || "").split(":");
    if (type === "monthly") commit("setPreviewMonthlyCode", code || "");
    if (type === "holiday") commit("setPreviewHolidayCode", code || "");
    if (!previewCode) {
      commit("setPreviewMonthlyCode", "");
      commit("setPreviewHolidayCode", "");
    }
    await dispatch("refresh");
  },

  async setMonthlyPreview({ commit, dispatch }, code) {
    commit("setPreviewMonthlyCode", code || "");
    await dispatch("refresh");
  },

  async setHolidayPreview({ commit, dispatch }, code) {
    commit("setPreviewHolidayCode", code || "");
    await dispatch("initialize");
  },

  scheduleRefresh({ state, commit, dispatch }) {
    if (state.refreshTimer) window.clearTimeout(state.refreshTimer);
    const nextChange = Date.parse(state.activeTheme.next_change_at || "");
    const untilChange = Number.isFinite(nextChange)
      ? Math.max(0, nextChange - Date.now() + 1000)
      : MAX_REFRESH_DELAY_MS;
    const delay = Math.max(
      MIN_REFRESH_DELAY_MS,
      Math.min(untilChange, MAX_REFRESH_DELAY_MS)
    );
    const timer = window.setTimeout(() => dispatch("refresh"), delay);
    commit("setRefreshTimer", timer);
  },

  setupLifecycle({ state, commit, dispatch }) {
    if (state.visibilityHandler) return;
    const handler = () => {
      if (document.visibilityState !== "visible") return;
      const currentLocalDate = localDateKey();
      const themeDate = state.activeTheme.local_date;
      const lastRefresh = Date.parse(state.lastRefreshAt || "");
      const stale =
        !Number.isFinite(lastRefresh) || Date.now() - lastRefresh > 5 * 60 * 1000;
      if (!themeDate || themeDate !== currentLocalDate || stale) {
        dispatch("refresh");
      }
    };
    document.addEventListener("visibilitychange", handler);
    commit("setVisibilityHandler", handler);
  },

  async reset({ state, commit, rootGetters }) {
    if (state.refreshTimer) window.clearTimeout(state.refreshTimer);
    if (state.visibilityHandler) {
      document.removeEventListener("visibilitychange", state.visibilityHandler);
    }
    ThemeCacheService.remove(userCacheKey(rootGetters));
    ThemeTokenService.reset();
    ThemeResourceService.reset();
    commit("resetState");
  },
};

const mutations = {
  setActiveTheme(state, value) {
    state.activeTheme = value;
  },
  setPreferences(state, value) {
    state.preferences = { ...freshPreferences(), ...value };
  },
  setEtag(state, value) {
    state.etag = value;
  },
  setInitializing(state, value) {
    state.isInitializing = value;
  },
  setLoadingTheme(state, value) {
    state.isLoadingTheme = value;
  },
  setLoadingPreferences(state, value) {
    state.isLoadingPreferences = value;
  },
  setInitialized(state, value) {
    state.initialized = value;
  },
  setUsingCachedTheme(state, value) {
    state.usingCachedTheme = value;
  },
  setThemeError(state, value) {
    state.errors.theme = value;
  },
  setPreferencesError(state, value) {
    state.errors.preferences = value;
  },
  setPreload(state, value) {
    state.preload = value;
  },
  setRefreshTimer(state, value) {
    state.refreshTimer = value;
  },
  setVisibilityHandler(state, value) {
    state.visibilityHandler = value;
  },
  setLastRefreshAt(state, value) {
    state.lastRefreshAt = value;
  },
  setPreviewMonthlyCode(state, value) {
    state.previewMonthlyCode = value;
  },
  setPreviewHolidayCode(state, value) {
    state.previewHolidayCode = value;
  },
  resetState(state) {
    state.activeTheme = freshClassicTheme();
    state.preferences = freshPreferences();
    state.etag = "";
    state.isInitializing = false;
    state.isLoadingTheme = false;
    state.isLoadingPreferences = false;
    state.initialized = false;
    state.usingCachedTheme = false;
    state.errors = { theme: null, preferences: null };
    state.preload = { total: 0, loaded: 0, failed: [] };
    state.refreshTimer = null;
    state.visibilityHandler = null;
    state.lastRefreshAt = null;
    state.previewMonthlyCode = "";
    state.previewHolidayCode = "";
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
